import type { Activity, CardView } from "@breeze/shared";
import { STAGE_LABELS } from "@breeze/shared";
import { useActivity } from "../hooks/useActivity.js";

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
}

interface RenderedActivity {
  icon: string;
  text: string;
  /** Tailwind classes for the icon chip. Defaults to neutral ink. Used
   *  to give high-signal events (payments) a colour cue. */
  chipClasses?: string;
  textClasses?: string;
}

function renderActivity(a: Activity): RenderedActivity {
  switch (a.type) {
    case "stage_change":
      return {
        icon: "→",
        text: `moved from ${a.fromStage ? STAGE_LABELS[a.fromStage] : "?"} to ${a.toStage ? STAGE_LABELS[a.toStage] : "?"}`,
      };
    case "note":
      return { icon: "✎", text: a.noteBody ?? "(empty note)" };
    case "message_ref":
      return { icon: "✉", text: `message #${a.msgId ?? "?"} attached` };
    case "system":
      return { icon: "◆", text: a.noteBody ?? "system event" };
    case "payment":
      // Money events deserve the eye — emerald chip, slightly brighter
      // text. Body already starts with "Payment:" / "Payment removed:"
      // etc. so the icon alone carries the cue.
      return {
        icon: "£",
        text: a.noteBody ?? "payment event",
        chipClasses: "bg-emerald-600/20 text-emerald-200 ring-1 ring-emerald-500/40",
        textClasses: "text-emerald-50",
      };
    default:
      return { icon: "•", text: a.type };
  }
}

export function ActivityTab({ card }: { card: CardView }): JSX.Element {
  const q = useActivity(card.cardId);
  const items = q.data?.activity ?? [];

  if (q.isLoading) {
    return (
      <div className="rounded-md border border-ink-700 bg-ink-900/40 px-4 py-6 text-center text-[13px] text-ink-200">
        loading activity…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <section className="rounded-md border border-dashed border-ink-500 bg-ink-900/40 px-4 py-8 text-center">
        <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-ink-50">
          Activity
        </div>
        <p className="mx-auto mt-2 max-w-xs text-[13px] text-ink-200">
          Stage changes, notes, messages, and system events will appear here.
        </p>
      </section>
    );
  }

  return (
    <ol className="flex flex-col gap-0">
      {items.map((a) => {
        const r = renderActivity(a);
        return (
          <li
            key={a.activityId}
            className="flex gap-3 border-l border-ink-700 pl-4 pb-3 pt-0.5 last:pb-0"
          >
            <span
              className={`-ml-[22px] flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[12px] ${
                r.chipClasses ?? "bg-ink-800 text-ink-50"
              }`}
              aria-hidden
            >
              {r.icon}
            </span>
            <div className="min-w-0 flex-1">
              <div
                className={`whitespace-pre-wrap break-words text-[13px] leading-snug ${
                  r.textClasses ?? "text-ink-50"
                }`}
              >
                {r.text}
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-[12px] text-ink-200">
                <span>{a.actor ?? "system"}</span>
                <span>·</span>
                <span
                  className="tabular-nums"
                  title={new Date(a.createdAt).toLocaleString()}
                >
                  {formatWhen(a.createdAt)}
                </span>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
